import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { useCallback } from 'react';

import { Bem } from '../../service/Bem/Bem';
import {
    extendTagValue,
    isTagExcluded,
    isTagIncluded,
    shrinkTagValue
} from '../../service/TagCloudTargetingHelper/TagCloudTargetingHelper';
import { TagValue, TargetingCloudProps, TargetingTagType, TargetingTagValue } from '../../typings/tagCloudTargeting';
import { Shape2Icon } from '../Shape2/Shape2';
import { TagCloud } from '../TagCloud/TagCloud';

import './EditableTargetingCloud.css';
import * as keyset from './EditableTargetingCloud.i18n';

const EXCLUDED_TAG_PREFIX = '\u2013 ';
const BLOCK = 'editable-targeting-cloud';
const i18n = i18nFactory(keyset);

export interface EditableTargetingCloudProps extends TargetingCloudProps {
    isEditable: boolean;
}

export const EditableTargetingCloud: React.FC<EditableTargetingCloudProps> = React.memo(props => {
    const { tags, type, isEditable, onAdd, onDelete, onChange, dictionaryItems, showUserValueOnEdit } = props;
    const includedTags = tags.filter(isTagIncluded).map(shrinkTagValue);
    const excludedTags = tags.filter(isTagExcluded).map(shrinkTagValue);

    const renderIncludedValue = isEditable || includedTags.length > 0;
    const renderExcludedValue = isEditable || excludedTags.length > 0;
    const renderNoData = !isEditable && includedTags.length === 0 && excludedTags.length === 0;

    const onIncludedTagsAdd = useCallback(
        (addedTags: TagValue[]) => {
            if (onAdd) {
                onAdd(addedTags.map(tag => extendTagValue(tag, TargetingTagType.INCLUDED)));
            }
        },
        [onAdd]
    );

    const onExcludedTagsAdd = useCallback(
        (addedTags: TagValue[]) => {
            if (onAdd) {
                onAdd(addedTags.map(tag => extendTagValue(tag, TargetingTagType.EXCLUDED)));
            }
        },
        [onAdd]
    );

    const onIncludedTagDelete = useCallback(
        (tag: TagValue) => {
            onDelete(extendTagValue(tag, TargetingTagType.INCLUDED));
        },
        [onDelete]
    );

    const onExcludedTagDelete = useCallback(
        (tag: TagValue) => {
            onDelete(extendTagValue(tag, TargetingTagType.EXCLUDED));
        },
        [onDelete]
    );

    const onTagChange = useCallback(
        (tag: TargetingTagValue, newValue: TagValue) => {
            if (onChange) {
                onChange(tag, newValue);
            }
        },
        [onChange]
    );

    return (
        <div className={Bem.cls(BLOCK)}>
            {renderNoData && <div className={Bem.cls(BLOCK, 'no-data')}>{i18n('noData')}</div>}

            {renderIncludedValue && (
                <TagCloud
                    tags={includedTags}
                    inputType={type}
                    onAdd={onIncludedTagsAdd}
                    onDelete={onIncludedTagDelete}
                    onChange={onTagChange}
                    isEditable={isEditable}
                    icon={Shape2Icon.includedValue}
                    dictionaryItems={dictionaryItems}
                    showUserValueOnEdit={showUserValueOnEdit}
                />
            )}

            {renderExcludedValue && (
                <TagCloud
                    tags={excludedTags}
                    inputType={type}
                    onAdd={onExcludedTagsAdd}
                    onDelete={onExcludedTagDelete}
                    onChange={onTagChange}
                    isEditable={isEditable}
                    tagPrefix={EXCLUDED_TAG_PREFIX}
                    icon={Shape2Icon.excludedValue}
                    dictionaryItems={dictionaryItems}
                    showUserValueOnEdit={showUserValueOnEdit}
                />
            )}
        </div>
    );
});
